"use client";

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useAuthStore } from "@/modules/auth/stores/auth-store";
import { useLocalStorage } from "@/hooks/use-local-storage";

import { getDashboard } from "../services/dashboard";

import type { DashboardResponse } from "@/types";

// The catalog changes on deploy, not on data, so it can sit stale for a long
// time. Only the widget DATA query refetches on activity.
const CATALOG_STALE_TIME = 30 * 60 * 1000;

// Baked into the storage key, so a response-shape change simply misses the old
// entry instead of restoring it into a component that no longer understands it.
// Bump this whenever DashboardResponse changes shape. (v2 dropped `data`: the
// catalog no longer carries payloads, since each card fetches its own. v3 added
// `max_items` / `options_source` to WidgetParam for the multi-select control.)
const STORE_VERSION = 3;

type StoredCatalog = DashboardResponse | null;

const storageKey = (companyUuid: string | undefined) =>
  `buro:dashboard-catalog:v${STORE_VERSION}:${companyUuid ?? "none"}`;

/**
 * Catalog + layout, restored from disk on the way in.
 *
 * TanStack holds this for 30 minutes, but that cache lives in memory and dies on
 * every hard refresh — which is exactly when the wait hurts. Keeping the last
 * response in localStorage lets a returning user paint their REAL layout
 * immediately: the right widgets, in the right slots, at the right sizes, each
 * showing its own skeleton while the data lands. No guessed placeholder, and no
 * blank page.
 *
 * This carries more weight now than it did. The catalog no longer ships any
 * payloads, so without it a returning user waits on the catalog request before a
 * single card can even start fetching — two sequential round trips before
 * anything appears. With it, the cards' requests go out on the first render and
 * the guessed DashboardSkeleton is never shown at all.
 *
 * It is placeholder data, never cached data, so the request still goes out and
 * the screen reconciles the moment it answers.
 */
export function useDashboard() {
  const companyUuid = useAuthStore((s) => s.companyAccount?.uuid);

  // useLocalStorage rather than a read in an effect: it is built on
  // useSyncExternalStore, so the value is available on the first client render
  // without a setState pass, and it degrades to `null` during SSR instead of
  // making the first render disagree with the server's HTML.
  const [stored, setStored] = useLocalStorage<StoredCatalog>(
    storageKey(companyUuid),
    null,
  );

  const restored = useMemo<DashboardResponse | undefined>(() => {
    if (!companyUuid || !isUsable(stored)) return undefined;

    // Only ever the LAYOUT, never any figures — the catalog holds no payloads to
    // restore, so there is nothing here that could put yesterday's numbers on
    // screen as though they were current. Each card fetches its own data,
    // starting immediately and in parallel with this revalidation rather than
    // waiting on it. Losing the sequential dependency is the win.
    return stored;
  }, [companyUuid, stored]);

  const query = useQuery<DashboardResponse>({
    queryKey: ["dashboard", "catalog", companyUuid],
    queryFn: getDashboard,
    enabled: !!companyUuid,
    staleTime: CATALOG_STALE_TIME,
    placeholderData: restored,
  });

  const { data, isPlaceholderData } = query;

  useEffect(() => {
    if (!companyUuid || !data || isPlaceholderData) return;

    // Listed field by field rather than spread, so a new field on the response
    // fails to compile here until someone decides whether it belongs on disk and
    // bumps STORE_VERSION. A spread would silently carry it — including, one day,
    // something payload-sized, and localStorage is a hard quota shared with the
    // rest of the app.
    setStored({
      widgets: data.widgets,
      layout: data.layout,
      visible: data.visible,
      slot_limits: data.slot_limits,
    });
  }, [companyUuid, data, isPlaceholderData, setStored]);

  return query;
}

/**
 * Anything hand-edited, half-written or from a shape we no longer expect is
 * discarded rather than rendered. The version key catches deliberate changes;
 * this catches everything else, and the cost of being wrong is a component
 * crashing for precisely the users who had a dashboard before the deploy.
 */
function isUsable(stored: StoredCatalog): stored is DashboardResponse {
  return (
    !!stored &&
    Array.isArray(stored.widgets) &&
    Array.isArray(stored.visible) &&
    !!stored.layout &&
    !!stored.slot_limits
  );
}
